import { useWorkflowGraphState } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import { useQuery } from "@tanstack/react-query";
import {
  DropdownInput,
  FilterBuilder,
  FilterBuilderFieldsType,
  Form,
  Radio,
  Skeleton,
  useForm,
} from "@validereinc/common-components";
import { Resources, TemplatedReportAdapter } from "@validereinc/domain";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  LockReportFormValuesType,
  useWorkflowConfigurationFormStore,
} from "../WorkflowConfigurationFormStore";

type LockReportMode = "by_report_filter" | "by_report_id";

const WorkflowConfigurationLockReportDetails = ({
  stepId,
}: {
  stepId?: string;
}) => {
  const { setLockReport, setLockReportIsValid, getLockReportFormValues } =
    useWorkflowConfigurationFormStore();

  const storeValues = getLockReportFormValues();
  const previousTemplateNameRef = useRef<string | undefined>();

  const form = useForm<LockReportFormValuesType>({
    values: storeValues,
  });

  const formValues = form.watch();

  // Determine the current mode based on form values
  const currentMode: LockReportMode = formValues.report_id
    ? "by_report_id"
    : "by_report_filter";

  const [mode, setMode] = useState<LockReportMode>(currentMode);

  const [inputFilter, setInputFilter] = useState<Record<string, any>>(
    formValues.input ?? {}
  );

  const { getAvailableStepVariables } = useWorkflowGraphState();
  const availableVariables = getAvailableStepVariables(stepId ?? "");

  const { data: templatedReports, isLoading: isLoadingTemplatedReports } =
    useQuery(
      [Resources.TEMPLATED_REPORT],
      () => TemplatedReportAdapter.company.getList({}),
      {
        staleTime: 3 * 60 * 1000,
      }
    );

  // Fetch the selected templated report to get its input_schema
  const { data: selectedTemplatedReport, isLoading: isLoadingSelectedReport } =
    useQuery(
      [Resources.TEMPLATED_REPORT, formValues.templated_report_name],
      () => {
        if (!formValues.templated_report_name) {
          return;
        }

        return TemplatedReportAdapter.getOne({
          id: formValues.templated_report_name,
        });
      },
      {
        enabled: Boolean(
          formValues.templated_report_name && mode === "by_report_filter"
        ),
        staleTime: 3 * 60 * 1000,
      }
    );

  const inputSchema = selectedTemplatedReport?.data?.input_schema ?? {};

  // Convert input_schema to FilterBuilder field format
  const filterBuilderFields: FilterBuilderFieldsType = useMemo(() => {
    const fields: FilterBuilderFieldsType = {};

    Object.entries(inputSchema).forEach(([key, schema]) => {
      fields[key] = {
        data_type: "string", // All fields are string values for now
        display_name: schema.display_name ?? key,
      };
    });

    return fields;
  }, [inputSchema]);

  // Initialize filter state from form values when they change (e.g., from store)
  useEffect(() => {
    setInputFilter(formValues.input ?? {});
  }, [JSON.stringify(formValues.input)]);

  // Update form when filter state changes
  useEffect(() => {
    if (mode === "by_report_filter") {
      form.setValue("input", inputFilter);
    }
  }, [inputFilter, mode]);

  // Handle mode changes
  const handleModeChange = (newMode: string) => {
    if (newMode === "by_report_id") {
      // Clear template-related fields when switching to report_filter mode
      form.setValue("templated_report_name", "");
      form.setValue("input", {});
      setInputFilter({});
    } else {
      // Clear report_id when switching to template mode
      form.setValue("report_id", "");
    }
    setMode(newMode as LockReportMode);
  };

  useEffect(() => {
    handleModeChange(currentMode);
  }, [currentMode]);

  useEffect(() => {
    if (stepId) {
      previousTemplateNameRef.current = undefined;
    }
  }, [stepId]);

  // Reset input to empty object when templated report name changes
  useEffect(() => {
    const currentTemplateName = formValues.templated_report_name;
    const previousTemplateName = previousTemplateNameRef.current;

    // Reset to empty object when template name actually changes
    if (
      currentTemplateName &&
      previousTemplateName !== undefined &&
      previousTemplateName !== currentTemplateName
    ) {
      setInputFilter({});
    }

    // Update the ref to track the current template name
    previousTemplateNameRef.current = currentTemplateName;
  }, [formValues.templated_report_name]);

  useEffect(() => {
    setLockReport(formValues);
  }, [JSON.stringify(formValues), setLockReport]);

  useEffect(() => {
    setLockReportIsValid(form.formState.isValid);
  }, [form.formState.isValid, setLockReportIsValid]);

  const isLoading = isLoadingTemplatedReports || isLoadingSelectedReport;

  const modeOptions = [
    { label: "By report template and inputs", value: "by_report_filter" },
    { label: "By report ID", value: "by_report_id" },
  ];

  return (
    <>
      <Form {...form}>
        <Radio
          name="mode"
          options={modeOptions}
          value={mode}
          onChange={handleModeChange}
          labelKey="label"
          valueKey="value"
          style={{ marginBottom: 16 }}
        />

        {mode === "by_report_filter" && (
          <DropdownInput
            label="Templated Report"
            name="templated_report_name"
            options={templatedReports?.data ?? []}
            placeholder="Select Templated Report..."
            isSortedAlphabetically
            valueKey="name"
            labelKey="display_name"
            isRequired
            isFluid
            isLoading={isLoadingTemplatedReports}
          />
        )}

        {mode === "by_report_id" && (
          <DropdownInput
            name="report_id"
            label="Report ID"
            options={availableVariables.map((value) => ({
              label: value,
              value,
            }))}
            placeholder="Select Report ID Variable..."
            isRequired
            isFluid
            labelKey="label"
            valueKey="value"
            isSearchable
          />
        )}
      </Form>

      {mode === "by_report_filter" && formValues.templated_report_name && (
        <Skeleton
          isLoading={isLoading}
          style={{ minHeight: 200 }}
        >
          {Object.keys(filterBuilderFields).length > 0 && (
            <FilterBuilder
              filter={inputFilter}
              setFilter={setInputFilter}
              fields={filterBuilderFields}
              title="Input Values"
              variant="vertical"
            />
          )}
        </Skeleton>
      )}
    </>
  );
};

export default WorkflowConfigurationLockReportDetails;
